<template>
  <div id="materialOrderView">
    <div id="view-sub-nav">
      <v-spacer></v-spacer>
      <v-btn
        @click="$utils.copyToClipboard(frontsText.concat(insertsText).concat(worktopsText).concat(edgeTreatmentText))"
        color="primary"
        variant="outlined"
        :disabled="!hasSavedSnapshot"
        >Copy to Clipboard</v-btn
      >
      <v-btn @click="textToEmail" color="info" variant="outlined" class="ml-2" :disabled="!hasSavedSnapshot"
        >Text to Email</v-btn
      >
      <v-btn @click="openSaveDialog" color="success" variant="outlined" class="ml-2" :disabled="hasSavedSnapshot"
        >Save Material Data</v-btn
      >
    </div>
    <v-container fluid class="py-0">
      <v-row>
        <v-col cols="12" class="text-right"> </v-col>
        <v-col cols="12">
          <v-card class="mb-3">
            <v-card-title class="bg-primary text-white">
              <span v-if="version">Sheets</span>
            </v-card-title>
            <v-card-text>
              <v-data-table
                :items="materials.concat(inserts, pantryLamBase, spiceRackLaminateDiff, copiedMaterial)"
                :headers="headers"
                :items-per-page="-1"
                hide-default-footer
              >
                <template v-slot:item="props">
                  <tr v-if="props.item.qty > 0">
                    <td>{{ props.item.qty }}</td>
                    <td
                      @click="
                        () => {
                          selectedMaterial = props.item
                          qtyOverrideDialog = true
                        }
                      "
                    >
                      <div v-if="props.item.qtyOverride || props.item.qtyOverrideOffcut">
                        {{ props.item.qtyOverrideOffcut ? 'Offcut Used' : props.item.qtyOverride }}
                      </div>
                      <div v-else class="text-disabled">Override sheet qty</div>
                    </td>
                    <td>{{ props.item.thickness }}</td>
                    <td>{{ props.item.name }}</td>
                    <td>{{ props.item.dimensions }}</td>
                    <td>{{ props.item.hasGrain ? 'n/a' : props.item.sides == 2 ? 'Double' : 'Single' }}</td>
                    <td>
                      <v-select
                        :items="props.item.sheetSizes"
                        :model-value="props.item.sheetOverride"
                        placeholder="Override sheet size"
                        hide-details
                        class="hide-line mt-0 pt-0 text-body-2"
                        @focus="selectedMaterial = props.item"
                        @click:clear="selectedMaterial = props.item"
                        @update:model-value="overrideSheetSize"
                        :clearable="props.item.isCopy ? false : true"
                        clear-icon="mdi-close-circle"
                      >
                      </v-select>
                    </td>
                    <td class="text-right">{{ props.item.colour }}</td>
                    <td class="d-flex flex-row justify-end align-center">
                      <v-tooltip location="top" :open-delay="500">
                        <template v-slot:activator="{ props: activatorProps }">
                          <v-icon
                            size="small"
                            :disabled="
                              !props.item.colour ||
                              props.item.isCopy ||
                              props.item.sections ||
                              !['18mm'].includes(props.item.thickness) ||
                              props.item.sheetSizes.length == 0
                            "
                            @click="
                              () => {
                                selectedMaterial = props.item
                                copyMaterial()
                              }
                            "
                            v-bind="activatorProps"
                            >mdi-content-copy</v-icon
                          >
                        </template>
                        <span>Copy</span>
                      </v-tooltip>
                      <v-tooltip location="top" :open-delay="500">
                        <template v-slot:activator="{ props: activatorProps }">
                          <v-icon
                            size="small"
                            class="ml-2"
                            :disabled="!props.item.isCopy"
                            @click="
                              () => {
                                selectedMaterial = props.item
                                deleteCopyMaterial()
                              }
                            "
                            v-bind="activatorProps"
                            >mdi-delete</v-icon
                          >
                        </template>
                        <span>Delete</span>
                      </v-tooltip>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" v-if="worktops.length > 0">
          <v-card>
            <v-card-title class="bg-primary text-white">
              <span>Worktops</span>
            </v-card-title>
            <v-card-text>
              <!-- Visual Worktop Sections -->
              <div v-for="(worktop, wIndex) in worktops" :key="'worktop-' + wIndex" class="worktop-visual mt-6">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="text-subtitle-2">
                    {{ worktop.thickness + 'mm' }}
                    {{ worktop.material }} {{ worktop.faces === 'single' ? '(Single-Sided)' : '(Double-Sided)' }} -
                    {{ worktop.colourName }}
                    {{ worktop.colourCode }}
                  </div>
                  <div class="text-caption">Sheet Size: {{ worktop.size }}</div>
                </div>
                <v-divider class="mb-3"></v-divider>
                <div
                  v-for="(section, sIndex) in worktop.sections"
                  :key="'section-' + sIndex"
                  class="worktop-section-item"
                >
                  <v-row class="align-center">
                    <!-- Section Name -->
                    <v-col cols="1" class="section-name-col">
                      <div class="section-name">{{ section.name }}</div>
                      <div v-if="section.dogleg" class="section-type">
                        {{ section.dogleg ? '(Dogleg)' : '(Upstand)' }}
                      </div>
                      <div v-if="section.customCoreThickness" class="section-type">
                        {{ '(' + section.coreThickness + 'mm' + ' Upstand)' }}
                      </div>
                      <div v-if="section.displayVerticalLeft || section.displayVerticalRight" class="section-vertical">
                        <v-icon size="small">{{
                          section.displayVerticalRight ? 'mdi-arrow-collapse-right' : 'mdi-arrow-collapse-left'
                        }}</v-icon>
                      </div>
                    </v-col>

                    <!-- Edge Labels -->
                    <v-col
                      cols="1"
                      class="pr-4"
                      :class="section.verticallyRotated ? 'edge-labels-rotated' : 'edge-labels'"
                    >
                      <div>
                        <div class="label">Back</div>
                        <div class="rule"></div>
                      </div>
                      <div>
                        <div class="label">Front</div>
                        <div class="rule"></div>
                      </div>
                    </v-col>

                    <!-- Finishing Box -->
                    <v-col cols="2" class="d-flex justify-center">
                      <div class="worktop-box" :class="{ 'has-dogleg': section.dogleg }">
                        <div
                          :class="
                            section.dogleg && !section.verticallyRotated
                              ? 'label-shifted'
                              : section.dogleg && section.verticallyRotated
                              ? 'label-rotated'
                              : 'label'
                          "
                        >
                          Finishing
                        </div>
                        <v-checkbox
                          class="ma-0 pa-0"
                          :class="section.verticallyRotated ? 'back-rotated' : 'back'"
                          disabled
                          v-model="section.finished.back"
                          hide-details
                        />
                        <v-checkbox
                          class="ma-0 pa-0"
                          :class="
                            section.dogleg && !section.verticallyRotated
                              ? 'front-shifted'
                              : section.dogleg && section.verticallyRotated
                              ? 'front-shifted-rotated'
                              : section.verticallyRotated
                              ? 'front-rotated'
                              : 'front'
                          "
                          disabled
                          v-model="section.finished.front"
                          hide-details
                        />
                        <v-checkbox
                          class="ma-0 pa-0"
                          :class="
                            section.dogleg && !section.verticallyRotated
                              ? 'left-shifted'
                              : section.dogleg && section.verticallyRotated
                              ? 'left-shifted-rotated'
                              : section.verticallyRotated
                              ? 'left-rotated'
                              : 'edge-left'
                          "
                          disabled
                          v-model="section.finished.left"
                          hide-details
                        />
                        <v-checkbox
                          class="ma-0 pa-0"
                          :class="section.verticallyRotated ? 'right-rotated' : 'edge-right'"
                          disabled
                          v-model="section.finished.right"
                          hide-details
                        />
                        <div v-if="section.dogleg" :class="section.verticallyRotated ? 'innerBox-rotated' : 'innerBox'">
                          <div :class="section.verticallyRotated ? 'innerLabel-rotated' : 'innerLabel'">Dogleg</div>
                          <v-checkbox
                            class="ma-0 pa-0"
                            :class="section.verticallyRotated ? 'dogLong-rotated' : 'dogLong'"
                            disabled
                            v-model="section.finished.dogLong"
                            hide-details
                          />
                          <v-checkbox
                            class="ma-0 pa-0"
                            :class="section.verticallyRotated ? 'dogShort-rotated' : 'dogShort'"
                            disabled
                            v-model="section.finished.dogShort"
                            hide-details
                          />
                        </div>
                      </div>
                    </v-col>

                    <!-- Edge-banding Box -->
                    <v-col cols="2" class="d-flex justify-center">
                      <div class="worktop-box" :class="{ 'has-dogleg': section.dogleg }">
                        <div
                          :class="
                            section.dogleg && !section.verticallyRotated
                              ? 'label-shifted'
                              : section.dogleg && section.verticallyRotated
                              ? 'label-rotated'
                              : 'label'
                          "
                        >
                          Edge-banding
                        </div>
                        <v-checkbox
                          class="ma-0 pa-0"
                          :class="section.verticallyRotated ? 'back-rotated' : 'back'"
                          disabled
                          v-model="section.edgeBanded.back"
                          hide-details
                        />
                        <v-checkbox
                          class="ma-0 pa-0"
                          :class="
                            section.dogleg && !section.verticallyRotated
                              ? 'front-shifted'
                              : section.dogleg && section.verticallyRotated
                              ? 'front-shifted-rotated'
                              : section.verticallyRotated
                              ? 'front-rotated'
                              : 'front'
                          "
                          disabled
                          v-model="section.edgeBanded.front"
                          hide-details
                        />
                        <v-checkbox
                          class="ma-0 pa-0"
                          :class="
                            section.dogleg && !section.verticallyRotated
                              ? 'left-shifted'
                              : section.dogleg && section.verticallyRotated
                              ? 'left-shifted-rotated'
                              : section.verticallyRotated
                              ? 'left-rotated'
                              : 'edge-left'
                          "
                          disabled
                          v-model="section.edgeBanded.left"
                          hide-details
                        />
                        <v-checkbox
                          class="ma-0 pa-0"
                          :class="section.verticallyRotated ? 'right-rotated' : 'edge-right'"
                          disabled
                          v-model="section.edgeBanded.right"
                          hide-details
                        />
                        <div v-if="section.dogleg" :class="section.verticallyRotated ? 'innerBox-rotated' : 'innerBox'">
                          <div :class="section.verticallyRotated ? 'innerLabel-rotated' : 'innerLabel'">Dogleg</div>
                          <v-checkbox
                            class="ma-0 pa-0"
                            :class="section.verticallyRotated ? 'dogLong-rotated' : 'dogLong'"
                            disabled
                            v-model="section.edgeBanded.dogLong"
                            hide-details
                          />
                          <v-checkbox
                            class="ma-0 pa-0"
                            :class="section.verticallyRotated ? 'dogShort-rotated' : 'dogShort'"
                            disabled
                            v-model="section.edgeBanded.dogShort"
                            hide-details
                          />
                        </div>
                      </div>
                    </v-col>

                    <!-- Radius Box -->
                    <v-col cols="2" class="d-flex justify-center">
                      <div class="worktop-box radius-box" :class="{ 'has-dogleg': section.dogleg }">
                        <div
                          :class="
                            section.dogleg && !section.verticallyRotated
                              ? 'label-shifted'
                              : section.dogleg && section.verticallyRotated
                              ? 'label-rotated'
                              : 'label'
                          "
                        >
                          Radius (mm)
                        </div>
                        <span :class="section.verticallyRotated ? 'bl-rotated' : 'bl'">{{
                          section.radii?.bl || 0
                        }}</span>
                        <span :class="section.verticallyRotated ? 'br-rotated' : 'br'">{{
                          section.radii?.br || 0
                        }}</span>
                        <span :class="section.verticallyRotated ? 'fr-rotated' : 'fr'">{{
                          section.radii?.fr || 0
                        }}</span>
                        <span
                          :class="
                            section.dogleg && !section.verticallyRotated
                              ? 'fl-dog'
                              : section.dogleg && section.verticallyRotated
                              ? 'fl-dog-rotated'
                              : section.verticallyRotated
                              ? 'fl-rotated'
                              : 'fl'
                          "
                          >{{ section.radii?.fl || 0 }}</span
                        >
                        <div v-if="section.dogleg" :class="section.verticallyRotated ? 'innerBox-rotated' : 'innerBox'">
                          <div :class="section.verticallyRotated ? 'innerLabel-rotated' : 'innerLabel'">Dogleg</div>
                          <span :class="section.verticallyRotated ? 'i-rotated' : 'i'">{{
                            section.radii?.i || 5
                          }}</span>
                        </div>
                      </div>
                    </v-col>

                    <!-- Dimensions Box -->
                    <v-col cols="2" class="d-flex justify-center">
                      <div class="worktop-box dimensions-box" :class="{ 'has-dogleg': section.dogleg }">
                        <div
                          :class="
                            section.dogleg && !section.verticallyRotated
                              ? 'label-shifted'
                              : section.dogleg && section.verticallyRotated
                              ? 'label-rotated'
                              : 'label'
                          "
                        >
                          Dimensions (mm)
                        </div>
                        <span :class="section.verticallyRotated ? 'depth-rotated' : 'depth'">D{{ section.depth }}</span>
                        <span
                          :class="
                            section.dogleg && !section.verticallyRotated
                              ? 'length-shifted'
                              : section.dogleg && section.verticallyRotated
                              ? 'length-shifted-rotated'
                              : section.verticallyRotated
                              ? 'length-rotated'
                              : 'length'
                          "
                          >L{{ section.length }}</span
                        >
                        <div v-if="section.dogleg" :class="section.verticallyRotated ? 'innerBox-rotated' : 'innerBox'">
                          <div :class="section.verticallyRotated ? 'innerLabel-rotated' : 'innerLabel'">Dogleg</div>
                          <span :class="section.verticallyRotated ? 'doglegDepth-rotated' : 'doglegDepth'"
                            >D{{ section.doglegDepth }}</span
                          >
                          <span :class="section.verticallyRotated ? 'doglegLength-rotated' : 'doglegLength'"
                            >L{{ section.doglegLength }}</span
                          >
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" v-if="edgeTreatmentPanels.length > 0">
          <v-card>
            <v-card-title class="bg-primary text-white">
              <span>Edge Treatment</span>
            </v-card-title>
            <v-card-text>
              <div v-for="(group, materialKey) in groupedEdgeTreatmentPanels" :key="materialKey" class="mb-6">
                <div class="text-caption py-2 material-heading">{{ materialKey }}</div>
                <v-divider class="mb-3"></v-divider>
                <div class="d-flex flex-wrap">
                  <div v-for="panel in group" :key="panel.cp.itemNum" class="edge-panel-item">
                    <div class="edge-panel-wrapper">
                      <div class="edge-treatment-box">
                        <div class="label">
                          <div class="edge-title">Edge-banding</div>
                          <div class="item-label">{{ panel.label }}</div>
                        </div>
                        <v-checkbox
                          hide-details
                          class="top ma-0 pa-0"
                          :disabled="true"
                          v-model="panel.cp.edges.top"
                        ></v-checkbox>
                        <v-checkbox
                          hide-details
                          class="bottom ma-0 pa-0"
                          :disabled="true"
                          v-model="panel.cp.edges.bottom"
                        ></v-checkbox>
                        <v-checkbox
                          hide-details
                          class="edge-left ma-0 pa-0"
                          :disabled="true"
                          v-model="panel.cp.edges.left"
                        ></v-checkbox>
                        <v-checkbox
                          hide-details
                          class="edge-right ma-0 pa-0"
                          :disabled="true"
                          v-model="panel.cp.edges.right"
                        ></v-checkbox>
                      </div>
                      <div class="height-label text-caption text-disabled">H{{ panel.cp.h }}mm</div>
                    </div>
                    <div class="width-label text-caption text-disabled text-center">W{{ panel.cp.w }}mm</div>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="qtyOverrideDialog" width="500">
      <v-card v-if="selectedMaterial">
        <v-card-title class="bg-primary text-white text-h6">Override Sheet Qty</v-card-title>
        <v-card-text class="pt-6">
          <v-progress-linear :indeterminate="true" v-if="selectedMaterial == null"></v-progress-linear>
          <v-checkbox v-if="selectedMaterial != null" label="Offcut Used" v-model="qtyOverrideOffcut"></v-checkbox>
          <v-text-field
            class="text-center"
            v-if="selectedMaterial != null && !qtyOverrideOffcut"
            v-model.number="qtyOverrideValue"
            placeholder="Number of sheets"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="text"
            @click="
              () => {
                qtyOverrideDialog = false
                qtyOverrideValue = null
              }
            "
            >Cancel</v-btn
          >
          <v-btn color="primary" variant="flat" @click="overrideSheetQty">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="saveDialog" width="800" scrollable>
      <v-card>
        <v-card-title class="bg-success text-white">
          {{ hasStaleSnapshot ? 'Override Saved Material Data' : 'Save Material Data' }}
        </v-card-title>
        <v-card-text class="pt-4" style="max-height: 500px">
          <div v-if="hasStaleSnapshot" class="mb-4">
            <div class="text-subtitle-2 text-error mb-2">Previous Save</div>
            <pre
              class="pa-3 bg-grey-lighten-4 text-body-2"
              style="white-space: pre-wrap; max-height: 200px; overflow-y: auto"
              >{{ version.materialSnapshot?.text }}</pre
            >
            <v-divider class="my-4"></v-divider>
            <div class="text-subtitle-2 text-success mb-2">New Material Data</div>
          </div>
          <pre class="pa-3 bg-grey-lighten-4 text-body-2" style="white-space: pre-wrap">{{ currentMaterialText }}</pre>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="saveDialog = false">Cancel</v-btn>
          <v-btn color="success" variant="flat" @click="saveMaterialSnapshot" :loading="isSaving">
            {{ hasStaleSnapshot ? 'Override & Save' : 'Confirm Save' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Materials from '@/js/Materials'
import { db } from '@/js/db'
import { doc, updateDoc } from 'firebase/firestore'
import { prefetchMaterialsForVersion } from '@/js/MaterialResolver'
import { resolveWorktop, resolveWorktopColour } from '@/js/WorktopResolver'

export default {
  name: 'MaterialOrderView',
  props: ['shipment'],
  data() {
    return {
      quote: null,
      order: null,
      materialsReady: false,
      selectedMaterial: null,
      qtyOverrideDialog: false,
      qtyOverrideValue: null,
      qtyOverrideOffcut: false,
      saveDialog: false,
      isSaving: false,
      headers: [
        {
          title: 'Qty',
          key: 'qty',
          sort: false
        },
        {
          title: 'Qty Override',
          key: 'nested',
          sort: false
        },
        {
          title: 'Thickness',
          key: 'thickness',
          sort: false
        },
        {
          title: 'Material',
          key: 'name',
          sort: false
        },
        {
          title: 'Quote Sheet Size',
          key: 'dimensions',
          sort: false
        },
        {
          title: 'Quote Sheet Sides',
          key: 'sides',
          sort: false
        },
        {
          title: 'Sheet Size Override',
          key: 'nestedSheet',
          sort: false
        },
        {
          title: 'Colour',
          key: 'colour',
          sort: false,
          align: 'end'
        },
        {
          title: 'Action',
          key: 'action',
          sort: false,
          align: 'end'
        }
      ]
    }
  },
  async created() {
    await this.$live('quote', doc(db, 'quotes', this.shipment.quoteID))
    if (this.shipment.orderID) {
      await this.$live('order', doc(db, 'orders', this.shipment.orderID))
    } else {
      // US Order View
      await this.$live('order', doc(db, 'orders', this.shipment.id))
    }
    await prefetchMaterialsForVersion(this.version)
    this.materialsReady = true
  },
  computed: {
    version() {
      let v = null
      if (this.quote) {
        v = this.$utils.getVersionByID(this.quote.versions, this.shipment.versionID)
      }
      return v
    },
    materials() {
      var a = []
      if (!this.version || !this.materialsReady) return []
      for (let m of this.version.materials) {
        // Skip laminate-diff spice rack frames — handled by spiceRackLaminateDiff()
        if (
          this.version.spiceRacks &&
          this.version.spiceRacks.some((sr) => sr.materialType === 'laminate-diff' && sr.frame === m.id)
        ) {
          continue
        }
        if (
          this.version.materialUsage[m.id] &&
          this.version.materialUsage[m.id].areaWithWastage > 0 &&
          m.thickness !== 4
        ) {
          let material = Materials.getSheetSizesForType(m)
          let sheetSizes = (material?.sizes || []).filter((s) => {
            if (s.w !== m.w && s.h !== m.w) {
              if (material.hasGrain) {
                return s.thickness === m.thickness
              }
              return s.thickness === m.thickness && s.sides === m.sides
            }
          })

          const sheetSizesOptions = []
          for (const size of sheetSizes) {
            if (material.hasGrain) {
              sheetSizesOptions.push({
                text: size.h + ' x ' + size.w,
                value: size
              })
            } else {
              sheetSizesOptions.push({
                text: size.h + ' x ' + size.w + ' - ' + (size.sides === 1 ? 'Single' : 'Double'),
                value: size
              })
            }
          }

          var o = {
            id: m.id,
            qty: this.$utils.toFixedNumber(this.version.materialUsage[m.id].areaWithWastage / (m.w * m.h), 3),
            thickness: m.thickness + 'mm',
            name: m.label,
            dimensions: m.h + ' x ' + m.w,
            sides: m.sides,
            hasGrain: material?.hasGrain || false,
            colour: m.colourCode,
            qtyOverride: this.version.materialUsage[m.id].qtyOverride,
            qtyOverrideOffcut: this.version.materialUsage[m.id].qtyOverrideOffcut,
            sheetOverride: this.version.materialUsage[m.id].sheetOverride,
            sheetSizes: sheetSizesOptions
          }
          a.push(o)
        }
      }

      return a
    },
    inserts() {
      if (!this.version || this.version.fronts.length == 0 || this.version.handles.length == 0) return []

      var a = []
      for (let handle of this.version.handles) {
        if (handle.insert) {
          const insertMaterial = this.version.materials.find((m) => {
            return m.id === handle.insert
          })

          // We're not calculating exact usage of inserts so just 1 sheet per unique material
          if (insertMaterial.requiresColourCode && a.filter((e) => e.id === insertMaterial.id).length === 0) {
            a.push({
              id: insertMaterial.id,
              qty: 1,
              thickness: insertMaterial.thickness + 'mm',
              name: insertMaterial.label,
              dimensions: insertMaterial.h + ' x ' + insertMaterial.w,
              colour: insertMaterial.colourCode
            })
          }
        }
      }
      return a
    },
    pantryLamBase() {
      if (!this.version || !this.version?.pantries || this.shipment.region == 'us') return []

      let a = []
      for (let pantry of this.version.pantries) {
        if (pantry.base && pantry.carcass != pantry.base) {
          const pantryBaseMaterial = this.version.materials.find((m) => {
            return m.id === pantry.base
          })

          if (
            a.filter((e) => e.id === pantryBaseMaterial.id).length == 0 &&
            this.materials.findIndex((material) => material.id === pantryBaseMaterial.id) < 0
          ) {
            a.push({
              id: pantryBaseMaterial.id,
              qty: this.$utils.toFixedNumber(
                this.version.materialUsage[pantry.base].areaWithWastage / (pantryBaseMaterial.w * pantryBaseMaterial.h),
                3
              ),
              thickness: pantryBaseMaterial.thickness + 'mm',
              name: pantryBaseMaterial.label,
              dimensions: pantryBaseMaterial.h + ' x ' + pantryBaseMaterial.w,
              colour: pantryBaseMaterial.colourCode
            })
          }
        }
      }
      return a
    },
    spiceRackLaminateDiff() {
      if (!this.version || !this.version?.spiceRacks || this.shipment.region == 'us') return []

      let a = []
      for (let spiceRack of this.version.spiceRacks) {
        if (spiceRack.materialType !== 'laminate-diff') continue

        const frameMaterial = this.version.materials.find((m) => {
          return m.id === spiceRack.frame
        })

        if (
          frameMaterial &&
          a.filter((e) => e.id === frameMaterial.id).length == 0 &&
          this.materials.findIndex((material) => material.id === frameMaterial.id) < 0
        ) {
          // Build sheet size override options
          let materialType = Materials.getSheetSizesForType(frameMaterial)
          let sheetSizesOptions = []

          // Add sheet sizes from the database, excluding the quote's own sheet size
          if (materialType?.sizes) {
            let sheetSizes = materialType.sizes.filter((s) => {
              if (s.w !== frameMaterial.w && s.h !== frameMaterial.w) {
                return s.thickness === frameMaterial.thickness && s.sides === frameMaterial.sides
              }
            })
            for (const size of sheetSizes) {
              sheetSizesOptions.push({
                text: size.h + ' x ' + size.w + ' - ' + (size.sides === 1 ? 'Single' : 'Double'),
                value: size
              })
            }
          }

          const usage = this.version.materialUsage[spiceRack.frame]
          const sheetOverride = usage?.sheetOverride || null
          const sheetW = sheetOverride ? sheetOverride.w : frameMaterial.w
          const sheetH = sheetOverride ? sheetOverride.h : frameMaterial.h

          const area = usage?.areaWithWastage || 0

          a.push({
            id: frameMaterial.id,
            qty: parseFloat((area / (sheetW * sheetH)).toFixed(2)),
            thickness: frameMaterial.thickness + 'mm',
            name: frameMaterial.label,
            dimensions: frameMaterial.h + ' x ' + frameMaterial.w,
            sides: frameMaterial.sides,
            hasGrain: materialType?.hasGrain || false,
            colour: frameMaterial.colourCode,
            qtyOverride: usage?.qtyOverride,
            qtyOverrideOffcut: usage?.qtyOverrideOffcut,
            sheetOverride: sheetOverride,
            sheetSizes: sheetSizesOptions
          })
        }
      }
      return a
    },
    copiedMaterial() {
      if (!this.version || !this.version?.copyMaterial) return []

      let a = []

      for (let copy of this.version.copyMaterial) {
        let sheetSizesOptions = []
        let copyHasGrain = false
        for (let m of this.version.materials) {
          if (
            this.version.materialUsage[m.id] &&
            this.version.materialUsage[m.id].areaWithWastage > 0 &&
            m.thickness === 18 &&
            m.label === copy.name
          ) {
            let material = Materials.getSheetSizesForType(m)
            copyHasGrain = material?.hasGrain || false
            let sheetSizes = (material?.sizes || []).filter((s) => {
              if (s.w !== m.w && s.h !== m.w) {
                if (material.hasGrain) {
                  return s.thickness === m.thickness
                }
                return s.thickness === m.thickness && s.sides === m.sides
              }
            })

            for (const size of sheetSizes) {
              if (material.hasGrain) {
                sheetSizesOptions.push({
                  text: size.h + ' x ' + size.w,
                  value: size
                })
              } else {
                sheetSizesOptions.push({
                  text: size.h + ' x ' + size.w + ' - ' + (size.sides === 1 ? 'Single' : 'Double'),
                  value: size
                })
              }
            }
          }
        }

        // Find the original material to get its sides value
        const originalMaterial = this.version.materials.find((m) => m.id === copy?.id?.split('-')[0])

        a.push({
          id: copy?.id,
          qty: copy?.qty,
          thickness: copy?.thickness,
          name: copy?.name,
          dimensions: copy?.dimensions,
          sides: originalMaterial?.sides,
          hasGrain: copyHasGrain,
          colour: copy?.colour,
          isCopy: copy?.isCopy,
          sheetSizes: sheetSizesOptions,
          qtyOverride: copy?.qtyOverride,
          qtyOverrideOffcut: copy?.qtyOverrideOffcut,
          sheetOverride: copy?.sheetOverride
        })
      }

      return a
    },
    worktops() {
      if (!this.version) return []

      var a = []
      for (const w of this.version.worktops) {
        const worktopMaterial = resolveWorktop(w, this.quote.region)
        const colour = resolveWorktopColour(w, this.quote.region)

        let sheetSize = ''
        if (w.size) {
          if (w.size.includes('x')) {
            sheetSize = w.size.split('x')[0] + ' x ' + w.size.split('x')[1]
          } else {
            sheetSize = w.size == 'regular' ? '1220 x 2440' : '1300 x 3050'
          }
        }

        var o = {
          material: (worktopMaterial && worktopMaterial.text) || w.material || '',
          colour: w.colourCode,
          colourName: (colour && colour.name) || w.colourCode || '',
          thickness: w.thickness,
          faces: w.faces,
          size: sheetSize,
          sections: []
        }

        for (let s of w.sections) {
          var radiiString = !s.radii
            ? 'No radius specified'
            : 'Back Left ' +
              s.radii.bl +
              'mm  /  Back Right ' +
              s.radii.br +
              'mm  /  Front Right ' +
              s.radii.fr +
              'mm  /  Front Left ' +
              s.radii.fl +
              'mm'
          o.sections.push({
            name: s.name || `Section ${o.sections.length + 1}`,
            length: s.l,
            depth: s.d,
            dogleg: s.dogleg ? s.dogleg : null,
            doglegLength: s.dogleg && s.li ? s.li : null,
            doglegDepth: s.dogleg && s.di ? s.di : null,
            radiiString: radiiString,
            coreThickness: s.customCoreThickness ? s.coreThickness : null,
            customCoreThickness: s.customCoreThickness || false,
            displayVerticalLeft: s.displayVerticalLeft || false,
            displayVerticalRight: s.displayVerticalRight || false,
            verticallyRotated: s.displayVerticalLeft || s.displayVerticalRight || false,
            finished: s.finished || {
              front: false,
              back: false,
              left: false,
              right: false,
              dogLong: false,
              dogShort: false
            },
            edgeBanded: s.edgeBanded || {
              front: false,
              back: false,
              left: false,
              right: false,
              dogLong: false,
              dogShort: false
            },
            radii: s.radii || { fl: 0, fr: 0, bl: 0, br: 0, i: 5, fm: 0 }
          })
        }
        a.push(o)
      }
      return a
    },
    frontsText() {
      let s = 'Order Number ' + this.order.orderNumber + '\n\n' + 'Fronts' + '\n\n'

      let frontsOrderList = this.materials
        .concat(this.copiedMaterial)
        .concat(this.spiceRackLaminateDiff)
        .toSorted((a, b) => a.name - b.name)

      for (let i = 0; i < frontsOrderList.length; i++) {
        let m = frontsOrderList[i]

        if (m?.qtyOverrideOffcut) continue
        const sheets = m.qtyOverride ? m.qtyOverride : m.qty

        const sheetSize = m.sheetOverride
          ? m.sheetOverride.thickness + ' x ' + m.sheetOverride.h + ' x ' + m.sheetOverride.w
          : m.thickness + ' x ' + m.dimensions

        let sheetSides = m?.sides == 2 ? 'double sided' : 'single sided'
        if (m.sheetOverride?.sides) {
          sheetSides = m.sheetOverride.sides == 2 ? 'double sided' : 'single sided'
        }

        if (!m.colour) {
          s += sheets + ' off ' + m.name + ' on ' + sheetSize + ' B/BB' + '\n'
        } else if (m.hasGrain) {
          // Veneer materials - no sides info
          s += sheets + ' off ' + m.name + ' ' + m.colour + ' on ' + sheetSize + '\n'
        } else {
          if (m.name.split(' ').includes('Reeded')) {
            s += sheets + ' off ' + m.name.split(' ').slice(0, 3).join(' ') + ' on ' + sheetSize + '\n'
          } else if (sheetSides == 'single sided') {
            if (m.thickness == '18mm') {
              s +=
                sheets +
                ' off ' +
                m.name +
                ' ' +
                m.colour +
                ' on ' +
                sheetSize +
                ' (single-sided) birch ply (balancer back)' +
                '\n'
            } else {
              s +=
                sheets +
                ' off ' +
                m.name +
                ' ' +
                m.colour +
                ' on ' +
                sheetSize +
                ' (single-sided) birch ply (bare birch ply back)' +
                '\n'
            }
          } else {
            s += sheets + ' off ' + m.name + ' ' + m.colour + ' on ' + sheetSize + ' birch ply\n'
          }
        }
      }

      return s
    },
    insertsText() {
      var s = ''
      for (var i = 0; i < this.inserts.length; i++) {
        var m = this.inserts[i]

        let sheetSides = m?.sides == 2 ? ' double sided' : ' birch ply single sided (bare birch ply back)'
        if (m.sheetOverride?.sides) {
          sheetSides = m.sheetOverride.sides == 2 ? ' double sided' : ' birch ply single sided (bare birch ply back)'
        }

        s += m.qty + ' off ' + m.name + ' ' + m.colour + ' on ' + m.thickness + ' x ' + m.dimensions + sheetSides + '\n'
      }
      return s + '\n\n'
    },
    worktopsText() {
      if (this.worktops.length === 0) return ''
      let s = 'Worktops' + '\n\n'
      for (let i = 0; i < this.worktops.length; i++) {
        let w = this.worktops[i]
        s +=
          w.material +
          ' ' +
          w.colourName +
          ' / ' +
          w.colour +
          ' on ' +
          w.thickness +
          ' x ' +
          w.size +
          ' birch ply ' +
          w.faces +
          ' sided'
        if (w.faces == 'single') {
          s += ' with balancer\n\n'
        } else {
          s += '\n\n'
        }

        for (let ii = 0; ii < w.sections.length; ii++) {
          let sec = w.sections[ii]
          s += '1 off ' + sec.length + ' x ' + sec.depth + ' with radii ' + sec.radiiString
          if (sec.coreThickness) s += ' - Upstand section mounted on ' + sec.coreThickness + 'mm'
          if (sec.dogleg && sec.doglegLength && sec.doglegDepth)
            s +=
              ' - Dogleg section with cutout of ' +
              'L' +
              sec.doglegLength +
              'mm x ' +
              'D' +
              sec.doglegDepth +
              'mm (see DXF plan)'
          // Edge banding description
          if (sec.edgeBanded) {
            const edgeDesc = []
            if (sec.edgeBanded.front) edgeDesc.push(`Front Edge of L${sec.length}mm`)
            if (sec.edgeBanded.back) edgeDesc.push(`Back Edge of L${sec.length}mm`)
            if (sec.edgeBanded.left) edgeDesc.push(`Left Edge of D${sec.depth}mm`)
            if (sec.edgeBanded.right) edgeDesc.push(`Right Edge of D${sec.depth}mm`)
            if (sec.dogleg && sec.edgeBanded.dogLong) edgeDesc.push(`Dogleg Long Edge of L${sec.doglegLength}mm`)
            if (sec.dogleg && sec.edgeBanded.dogShort) edgeDesc.push(`Dogleg Short Edge of D${sec.doglegDepth}mm`)
            if (edgeDesc.length > 0) {
              s += ' - Edge-Banding on ' + edgeDesc.join(', ')
            }
          }
          s += '\n'
        }

        if (i < this.worktops.length - 1) s += '\n\n'
      }
      return s + '\n\n'
    },
    edgeTreatmentPanels() {
      if (!this.version || !this.version.coverPanels) return []
      return this.version.coverPanels.filter((cp) => {
        if (!cp.edgeMode || !cp.edges) return false
        return cp.edges.top || cp.edges.bottom || cp.edges.left || cp.edges.right
      })
    },
    groupedEdgeTreatmentPanels() {
      if (this.edgeTreatmentPanels.length === 0) return {}

      const grouped = {}
      for (const cp of this.edgeTreatmentPanels) {
        const material = this.version.materials.find((m) => m.id === cp.material)
        if (!material) continue

        const key = `${material.thickness}mm ${material.label}${material.colourCode ? ' - ' + material.colourCode : ''}`
        if (!grouped[key]) {
          grouped[key] = []
        }

        // Get panel reference label
        let label
        switch (cp.coverPanelType) {
          case 'end-b':
          case 'end-w':
            label = 'CP' + cp.itemNum
            break
          case 'end-b-l':
          case 'end-w-l':
            label = 'CPL' + cp.itemNum
            break
          case 'end-b-r':
          case 'end-w-r':
            label = 'CPR' + cp.itemNum
            break
          case 'v-plinth':
            label = 'VENT'
            break
          case 'plinth':
            label = 'PLINTH'
            break
          case 'filler':
            label = 'FP' + cp.itemNum
            break
          case 'back':
            label = 'BK' + cp.itemNum
            break
          case 'top':
            label = 'TP' + cp.itemNum
            break
          case 'underside':
            label = 'UP' + cp.itemNum
            break
          case 'custom':
            label = 'C' + cp.itemNum
            break
          case 'shelf':
            label = 'SHELF' + cp.itemNum
            break
          default:
            label = cp.itemNum?.toString() || 'UNKNOWN'
        }

        grouped[key].push({ cp, material, label })
      }
      return grouped
    },
    edgeTreatmentText() {
      if (this.edgeTreatmentPanels.length === 0) return ''

      // Build text output
      let s = 'Edge Treatment\n\n'

      for (const [heading, panels] of Object.entries(this.groupedEdgeTreatmentPanels)) {
        s += heading + '\n'

        for (const { cp, label } of panels) {
          // Build edge descriptions
          const edgeDescriptions = []
          if (cp.edges.top) edgeDescriptions.push(`Top Edge of W${cp.w}mm`)
          if (cp.edges.bottom) edgeDescriptions.push(`Bottom Edge of W${cp.w}mm`)
          if (cp.edges.left) edgeDescriptions.push(`Left Edge of H${cp.h}mm`)
          if (cp.edges.right) edgeDescriptions.push(`Right Edge of H${cp.h}mm`)

          const edgeText = edgeDescriptions.join(', ')
          s += `${label} - (W${cp.w}mm x H${cp.h}mm) - Edge-Banding on ${edgeText} [face-on perspective]\n`
        }

        s += '\n'
      }

      return s + '\n\n'
    },
    currentMaterialHash() {
      if (!this.version) return null
      const data = this.normalizeForHash({
        materials: this.version.materials,
        materialUsage: this.version.materialUsage,
        copyMaterial: this.version.copyMaterial || []
      })
      if (!data) return null // Guard for worktop-only orders
      const stableStringify = (obj) =>
        JSON.stringify(obj, (key, val) =>
          val && typeof val === 'object' && !Array.isArray(val)
            ? Object.keys(val)
                .sort()
                .reduce((acc, k) => {
                  acc[k] = val[k]
                  return acc
                }, {})
            : val
        )
      return this.simpleHash(stableStringify(data))
    },
    hasSavedSnapshot() {
      if (!this.version?.materialSnapshot) return false
      return this.version.materialSnapshot.hash === this.currentMaterialHash
    },
    hasStaleSnapshot() {
      if (!this.version?.materialSnapshot) return false
      return this.version.materialSnapshot.hash !== this.currentMaterialHash
    },
    currentMaterialText() {
      if (!this.order) return ''
      return this.frontsText.concat(this.insertsText).concat(this.worktopsText).concat(this.edgeTreatmentText)
    }
  },
  methods: {
    simpleHash(str) {
      let hash = 0
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i)
        hash = (hash << 5) - hash + char
        hash = hash & hash
      }
      return hash.toString(36)
    },
    normalizeForHash(obj) {
      // Recursively remove null, undefined, and empty values for consistent hashing
      if (obj === null || obj === undefined) return undefined
      if (Array.isArray(obj)) {
        const filtered = obj.map((item) => this.normalizeForHash(item)).filter((item) => item !== undefined)
        return filtered.length > 0 ? filtered : undefined
      }
      if (typeof obj === 'object') {
        const normalized = {}
        for (const [key, value] of Object.entries(obj)) {
          const normalizedValue = this.normalizeForHash(value)
          if (normalizedValue !== undefined) {
            normalized[key] = normalizedValue
          }
        }
        return Object.keys(normalized).length > 0 ? normalized : undefined
      }
      return obj
    },
    openSaveDialog() {
      this.saveDialog = true
    },
    async saveMaterialSnapshot() {
      this.isSaving = true
      try {
        const snapshot = {
          timestamp: this.$utils.tsNow(),
          hash: this.currentMaterialHash,
          text: this.currentMaterialText
        }

        const versionIndex = this.quote.versions.findIndex((v) => v.id === this.version.id)
        if (versionIndex > -1) {
          this.quote.versions[versionIndex].materialSnapshot = snapshot
          await updateDoc(doc(db, 'quotes', this.quote.id), {
            versions: this.quote.versions
          })
        }

        this.$store.commit('openSnackbar', { snackbarText: 'Material data saved' })
        this.saveDialog = false
      } finally {
        this.isSaving = false
      }
    },
    textToEmail() {
      const text = this.frontsText.concat(this.insertsText).concat(this.worktopsText).concat(this.edgeTreatmentText)
      const subject = encodeURIComponent('Order Number ' + this.order.orderNumber)
      const body = encodeURIComponent(text)
      window.open(`mailto:info@baldwinplastics.co.uk?subject=${subject}&body=${body}`)
    },
    async overrideSheetQty() {
      let copyIndex = this.version.copyMaterial?.findIndex((copy) => copy.id === this.selectedMaterial.id)
      if (copyIndex > -1) {
        this.version.copyMaterial[copyIndex].qtyOverride = this.qtyOverrideValue
        this.version.copyMaterial[copyIndex].qtyOverrideOffcut = this.qtyOverrideOffcut ? true : false
      } else {
        this.version.materialUsage[this.selectedMaterial.id].qtyOverride = this.qtyOverrideValue
        this.version.materialUsage[this.selectedMaterial.id].qtyOverrideOffcut = this.qtyOverrideOffcut ? true : false
      }
      await updateDoc(doc(db, 'quotes', this.quote.id), {
        versions: this.quote.versions
      })

      this.$store.commit('openSnackbar', { snackbarText: 'Sheet qty updated' })

      this.selectedMaterial = null
      this.qtyOverrideValue = null
      this.qtyOverrideOffcut = false
      this.qtyOverrideDialog = false
    },
    async overrideSheetSize(val) {
      let copyIndex = this.version.copyMaterial?.findIndex((copy) => copy.id === this.selectedMaterial.id)
      if (copyIndex > -1) {
        this.version.copyMaterial[copyIndex].sheetOverride = val
      } else {
        this.version.materialUsage[this.selectedMaterial.id].sheetOverride = val
      }
      await updateDoc(doc(db, 'quotes', this.quote.id), {
        versions: this.quote.versions
      })

      // We're not watching for changes so need to manually set on local variable
      this.$store.commit('openSnackbar', { snackbarText: 'Sheet size updated' })
      this.selectedMaterial = null
    },
    async copyMaterial() {
      // delete incase of undefined value
      this.selectedMaterial.qtyOverride = 1
      this.selectedMaterial.qtyOverrideOffcut = false
      this.selectedMaterial.sheetOverride = null

      // checking if theres already a copy
      let copyNum
      if (this.version?.copyMaterial && this.version?.copyMaterial.length > 0) {
        let foundCopyIndex = this.version?.copyMaterial.findLastIndex(
          (copy) => copy?.id.split('-')[0] === this.selectedMaterial.id
        )
        if (foundCopyIndex > -1) {
          copyNum = parseInt(this.version?.copyMaterial[foundCopyIndex].id.split('-')[1].slice(4))
          copyNum++
        }
      }

      // assigning copy number as uid and mark it as copy
      let cloneItem = Object.assign({}, this.selectedMaterial)
      if (copyNum) {
        cloneItem.isCopy = true
        cloneItem.id += '-COPY' + copyNum.toString()
      } else {
        cloneItem.isCopy = true
        cloneItem.id += '-COPY1'
      }

      // assign copy material's sheetOverride with initial sheetSizesOptions[0]
      let sheetSizesOptions = []
      for (let m of this.version.materials) {
        if (
          this.version.materialUsage[m.id] &&
          this.version.materialUsage[m.id].areaWithWastage > 0 &&
          m.thickness !== 4 &&
          m.label === this.selectedMaterial.name
        ) {
          let material = Materials.getSheetSizesForType(m)
          let sheetSizes = (material?.sizes || []).filter((s) => {
            if (s.w !== m.w && s.h !== m.w) {
              if (material.hasGrain) {
                return s.thickness === m.thickness
              }
              return s.thickness === m.thickness && s.sides === m.sides
            }
          })

          for (const size of sheetSizes) {
            if (material.hasGrain) {
              sheetSizesOptions.push({
                text: size.h + ' x ' + size.w,
                value: size
              })
            } else {
              sheetSizesOptions.push({
                text: size.h + ' x ' + size.w + ' - ' + (size.sides === 1 ? 'Single' : 'Double'),
                value: size
              })
            }
          }
        }
      }
      if (sheetSizesOptions) {
        // let firstAltSheetOption = sheetSizesOptions[0].value
        // cloneItem.sheetOverride = firstAltSheetOption
        // // change copy dimensions
        // cloneItem.dimensions = `${firstAltSheetOption.w} x ${firstAltSheetOption.h}`

        // change copy qty to 1
        cloneItem.qty = 1
      }

      if (!this.version?.copyMaterial || this.version?.copyMaterial.length == 0) {
        this.version.copyMaterial = []
        this.version.copyMaterial.push(cloneItem)
      } else {
        this.version.copyMaterial.push(cloneItem)
      }

      await updateDoc(doc(db, 'quotes', this.quote.id), {
        versions: this.quote.versions
      })

      // We're not watching for changes so need to manually set on local variable
      this.$store.commit('openSnackbar', { snackbarText: 'Copied Sheet Info (does not change quote)' })
      this.selectedMaterial = null
    },
    async deleteCopyMaterial() {
      if (this.selectedMaterial.isCopy) {
        let deleteIndex = this.version?.copyMaterial.findIndex((copy) => copy.id === this.selectedMaterial.id)
        if (deleteIndex > -1) this.version.copyMaterial.splice(deleteIndex, 1)
      }

      await updateDoc(doc(db, 'quotes', this.quote.id), {
        versions: this.quote.versions
      })

      // We're not watching for changes so need to manually set on local variable
      this.$store.commit('openSnackbar', { snackbarText: 'Copy Deleted' })

      this.selectedMaterial = null
    }
  }
}
</script>

<style lang="scss" scoped>
.hide-line :deep(.v-field__outline)::before {
  border: none;
}

.hide-line {
  --v-input-padding-top: 0px; // `0px`, not `0` — it is consumed inside a `calc()`

  :deep(.v-field--variant-underlined .v-field__append-inner),
  :deep(.v-field--variant-underlined .v-field__clearable) {
    align-items: center;
    padding-top: 0;
    padding-bottom: 0;
  }

  :deep(.v-field .v-field__input > input) {
    align-self: center;
  }
}

.material-heading {
  color: #333;
  font-weight: 500;
}

.edge-panel-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 24px;
}

.edge-panel-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.height-label {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
}

.width-label {
  width: 140px;
  margin-top: 8px;
}

.edge-treatment-box {
  position: relative;
  width: 140px;
  height: 70px;
  border: solid grey 1px;
  display: flex;
  align-items: center;
  justify-content: center;

  .label {
    text-align: center;

    .edge-title {
      font-size: 12px;
      font-weight: 500;
      color: #666;
    }

    .item-label {
      font-size: 10px;
      color: rgba(0, 0, 0, 0.6);
    }
  }

  .v-input {
    position: absolute;
    margin: 0;
    min-width: 20px;
    background: white;
    align-items: center;
    justify-content: center;

    // 🔴 V2 name; V3's is `.v-selection-control__input`. `margin-right` is dropped with the
    // rename: V3's selection control has no margin of its own to cancel.
    :deep(.v-selection-control__input) {
      transform: scale(0.7);
    }
  }

  .top {
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .bottom {
    bottom: 0;
    left: 50%;
    transform: translate(-50%, 50%);
  }

  .edge-left {
    left: 0%;
    transform: translateX(-50%);
  }

  .edge-right {
    right: 0%;
    transform: translateX(50%);
  }
}

.panel-dimensions {
  color: #666;
}

.worktop-section-item {
  padding-top: 24px;
  margin-bottom: 24px;
}

.section-name-col {
  .section-name {
    font-weight: 500;
    font-size: 12px;
    color: #333;
  }

  .section-type {
    font-size: 11px;
    color: #666;
  }

  .section-vertical {
    margin-top: 4px;
  }
}

.edge-labels {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 70px;

  > div {
    display: flex;
    align-items: center;

    .label {
      flex: 1;
      font-size: 12px;
    }

    .rule {
      flex: 1;
      height: 1px;
      background: grey;
    }
  }
}

.edge-labels-rotated {
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  height: 70px;

  > div {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;

    .label {
      flex: 1;
      font-size: 12px;
    }

    .rule {
      flex: 1;
      margin-left: 10px;
      float: left;
      width: 1px;
      height: 100%;
      background: grey;
    }
  }
}

.worktop-box {
  position: relative;
  width: 200px;
  height: 80px;
  border: solid grey 1px;
  display: flex;
  align-items: center;
  justify-content: center;

  .label {
    font-size: 12px;
    text-align: center;
  }

  .label-shifted {
    font-size: 12px;
    text-align: center;
    margin-bottom: 15%;
  }

  .label-rotated {
    font-size: 12px;
    text-align: center;
    margin-top: 12%;
  }

  .v-input {
    position: absolute;
    margin: 0;
    min-width: 20px;
    background: white;

    // 🔴 V2 name; V3's is `.v-selection-control__input`. `margin-right` is dropped with the
    // rename: V3's selection control has no margin of its own to cancel.
    :deep(.v-selection-control__input) {
      transform: scale(0.7);
    }
  }

  // Edge positions
  .back {
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .front {
    bottom: 0;
    left: 50%;
    transform: translate(-50%, 50%);
  }

  .front-shifted {
    bottom: 0;
    left: 75%;
    transform: translate(-50%, 50%);
  }

  .edge-left {
    left: 0%;
    transform: translateX(-50%);
  }

  .left-shifted {
    bottom: 50%;
    left: 0%;
    transform: translateX(-50%);
  }

  .edge-right {
    right: 0%;
    bottom: 50%;
    transform: translate(50%, 50%);
  }

  // Rotated edge positions
  .back-rotated {
    top: 50%;
    right: 0%;
    transform: translate(50%, -50%);
  }

  .front-rotated {
    bottom: 50%;
    left: 0%;
    transform: translate(-50%, 50%);
  }

  .front-shifted-rotated {
    bottom: 25%;
    left: 0;
    transform: translate(-50%, 50%);
  }

  .left-rotated {
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .left-shifted-rotated {
    top: 0%;
    left: 75%;
    transform: translate(-50%, -50%);
  }

  .right-rotated {
    right: 50%;
    bottom: 0%;
    transform: translate(50%, 50%);
  }

  // Dogleg inner box
  .innerBox {
    position: absolute;
    width: 50%;
    height: 50%;
    border-right: solid grey 1px;
    border-top: solid grey 1px;
    bottom: 0;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;

    .innerLabel {
      font-size: 10px;
      text-align: center;
    }
  }

  .innerBox-rotated {
    position: absolute;
    width: 50%;
    height: 50%;
    border-left: solid grey 1px;
    border-bottom: solid grey 1px;
    top: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    .innerLabel-rotated {
      font-size: 10px;
      text-align: center;
    }
  }

  .dogLong {
    top: 0%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .dogShort {
    top: 50%;
    right: 0%;
    transform: translate(50%, -50%);
  }

  .dogLong-rotated {
    bottom: 50%;
    left: 0%;
    transform: translate(-50%, 50%);
  }

  .dogShort-rotated {
    bottom: 0%;
    left: 50%;
    transform: translate(-50%, 50%);
  }

  // Radius box specific
  &.radius-box {
    span {
      position: absolute;
      font-size: 10px;
      background: white;
      padding: 0 2px;
    }

    .bl {
      top: 0;
      left: 0;
      transform: translate(-50%, -50%);
    }

    .br {
      top: 0;
      right: 0;
      transform: translate(50%, -50%);
    }

    .fr {
      bottom: 0;
      right: 0;
      transform: translate(50%, 50%);
    }

    .fl {
      bottom: 0;
      left: 0;
      transform: translate(-50%, 50%);
    }

    .fl-dog {
      top: 25%;
      left: 0;
      transform: translate(-50%, 50%);
    }

    .i {
      top: 0;
      right: 0;
      transform: translate(50%, -50%);
    }

    // Rotated radius positions
    .bl-rotated {
      top: 0;
      right: 0;
      transform: translate(50%, -50%);
    }

    .br-rotated {
      bottom: 0;
      right: 0;
      transform: translate(50%, 50%);
    }

    .fr-rotated {
      bottom: 0;
      left: 0;
      transform: translate(-50%, 50%);
    }

    .fl-rotated {
      top: 0;
      left: 0;
      transform: translate(-50%, -50%);
    }

    .fl-dog-rotated {
      top: 0;
      right: 25%;
      transform: translate(50%, -50%);
    }

    .i-rotated {
      bottom: 0;
      left: 0;
      transform: translate(-50%, 50%);
    }
  }

  // Dimensions box specific
  &.dimensions-box {
    span {
      position: absolute;
      font-size: 10px;
      background: white;
      padding: 0 2px;
    }

    .depth {
      right: 0;
      transform: translateX(50%);
    }

    .length {
      bottom: 0;
      left: 50%;
      transform: translate(-50%, 50%);
    }

    .length-shifted {
      top: 0;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    .doglegDepth {
      top: 50%;
      right: 0;
      transform: translate(50%, -50%);
    }

    .doglegLength {
      top: 0;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    // Rotated dimension positions
    .depth-rotated {
      bottom: 0;
      transform: translate(-50%, 50%);
      left: 50%;
    }

    .length-rotated {
      right: 0;
      top: 50%;
      transform: translate(50%, -50%);
    }

    .length-shifted-rotated {
      left: 0;
      top: 50%;
      transform: translate(-50%, -50%);
    }

    .doglegDepth-rotated {
      left: 0;
      top: 50%;
      transform: translate(-50%, -50%);
    }

    .doglegLength-rotated {
      bottom: 0;
      left: 50%;
      transform: translate(-50%, 50%);
    }
  }
}

#view-sub-nav .v-btn--disabled {
  background-color: #e0e0e0 !important;
  color: #9e9e9e !important;
  border-color: #e0e0e0 !important;
}
</style>
